<template>
  <div class="justify-content-center" data-testid="saml-page">
    <div v-if="!!identityProvider">
      <div class="d-flex justify-content-between align-items-center i-mb-10">
        <div>
          <h4 class="i-text-heading-5 i-text-neutral-120 i-mb-2">
            Single Sign-On
          </h4>
          <p class="i-text-body-2 i-text-neutral-110 i-mb-2">
            Manage your SSO settings -
            <a
              :href="ssoHelpCenterLink"
              target="_blank"
              class="i-text-primary-110"
              >Learn more</a
            >
          </p>
        </div>
        <div class="d-flex align-items-center">
          <i-button
            class="i-mr-4"
            text="Delete"
            type="neutral"
            subtype="subtle"
            data-testid="delete-identity-provider-button"
            size="lg"
            @button-click="deleteIdentityProvider(identityProvider)"
          ></i-button>
          <i-button
            text="Edit"
            data-testid="edit-identity-provider-button"
            size="lg"
            @button-click="editIdentityProvider"
          ></i-button>
        </div>
      </div>
      <div class="d-flex flex-column i-gap-10">
        <div>
          <div class="d-flex align-items-center">
            <label class="i-text-subheading-2 i-text-neutral-120 i-mb-0 i-mr-2"
              >Entity ID</label
            >
            <info-icon
              description="This is the unique entity ID for the service provider for your Inscribe organisation."
            ></info-icon>
          </div>
          <i-copy-input
            :model-value="identityProvider.metadata_url"
            class="i-mt-2"
            @text-copied="onTextCopied"
          />
        </div>
        <div>
          <div class="d-flex align-items-center">
            <label class="i-text-subheading-2 i-text-neutral-120 i-mb-0 i-mr-2"
              >Single-Sign-On URL</label
            >
            <info-icon
              description="Use this URL for service provider initiated login."
            ></info-icon>
          </div>
          <i-copy-input
            :model-value="identityProvider.sp_single_sign_on_url"
            class="i-mt-2"
            @text-copied="onTextCopied"
          />
        </div>
        <div>
          <div class="d-flex align-items-center">
            <label class="i-text-subheading-2 i-text-neutral-120 i-mb-0 i-mr-2"
              >Assertion Consumer Service URL</label
            >
            <info-icon
              description="This is the URL where your identity provider should send assertions to Inscribe."
            ></info-icon>
          </div>
          <i-copy-input
            :model-value="identityProvider.acs_url"
            class="i-mt-2"
            @text-copied="onTextCopied"
          />
        </div>
        <div>
          <div class="d-flex align-items-center">
            <label class="i-text-subheading-2 i-text-neutral-120 i-mb-0 i-mr-2"
              >Metadata URL</label
            >
            <info-icon
              description="You can use this URL to access the unique metadata for the service provider connected to your Inscribe organisation to configure your identity provider."
            ></info-icon>
          </div>
          <i-copy-input
            :model-value="identityProvider.metadata_url"
            class="i-mt-2"
            @text-copied="onTextCopied"
          />
        </div>
        <div>
          <div class="d-flex align-items-center">
            <label class="i-text-subheading-2 i-text-neutral-120 i-mb-0 i-mr-2"
              >Identity Provider Certificate Fingerprint</label
            >
            <info-icon
              description="Inscribe expects assertions from your identity provider to be signed with this certificate."
            ></info-icon>
          </div>
          <i-copy-input
            :model-value="identityProvider.idp_cert_fingerprint"
            class="i-mt-2"
            @text-copied="onTextCopied"
          />
        </div>
        <div>
          <div class="d-flex align-items-center">
            <label class="i-text-subheading-2 i-text-neutral-120 i-mb-0 i-mr-2"
              >Service Provider Certificate</label
            >
            <info-icon
              description="Inscribe will sign assertions with this certificate if you require it"
            ></info-icon>
          </div>
          <i-copy-input
            :model-value="identityProvider.sp_cert"
            class="i-mt-2"
            @text-copied="onTextCopied"
          />
        </div>
      </div>

      <authentication-requirements />
    </div>
    <div
      v-else
      class="no-identity-provider d-flex align-items-center justify-content-center"
    >
      <empty-page
        icon="large-touch-id-user"
        title="Start by adding an Identity Provider"
        message="Single Sign-On (SSO) provides a single point of authentication, which happens at a secure identity provider."
        btn-text="Add Identity Provider"
        :link="ssoHelpCenterLink"
        :btn-click="newIdentityProvider"
      ></empty-page>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { iButton, iCopyInput, showToast } from '@inscribe/signal';
import EmptyPage from '@/shared/components/EmptyPage.vue';
import InfoIcon from '@/V2/components/InfoIcon';
import AuthenticationRequirements from './AuthenticationRequirements.vue';

export default {
  components: {
    AuthenticationRequirements,
    EmptyPage,
    'i-button': iButton,
    iCopyInput,
    InfoIcon,
  },
  data() {
    return {
      fields: [
        'name',
        'single_sign_on_url',
        'metadata_url',
        'acs_url',
        'options',
      ],
    };
  },
  computed: {
    ...mapState(['identityProvider']),
    ssoHelpCenterLink() {
      return 'https://help.inscribe.ai/en/articles/6615232';
    },
  },
  methods: {
    editIdentityProvider() {
      this.$router.push({
        name: 'saml-settings-edit-identity-provider-view',
        params: { identity_provider_id: this.identityProvider.id },
      });
    },
    newIdentityProvider() {
      this.$router.push({
        name: 'saml-settings-new-identity-provider',
      });
    },
    deleteIdentityProvider(identityProvider) {
      if (confirm('Do you really want to delete this identity provder?')) {
        this.emitter.emit('preloader.on');
        this.$store.dispatch('deleteIdentityProvider').then(
          (response) => {
            this.emitter.emit('preloader.off');
            this.$store.commit('setIdentityProvider', undefined);
            showToast({
              text: 'Successfully deleted identity provider.',
            });
          },
          () => {
            showToast({
              text: 'Identity Provider could not be deleted, please try again later.',
              isError: true,
            });
          },
        );
      }
    },
    onTextCopied() {
      showToast({
        text: 'Value copied',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.wrap-fix {
  overflow-wrap: anywhere;
}
</style>
